import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, FormControlLabel, IconButton, Stack, Switch, TextField, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { StorefrontConfigDto, storefrontAdminApi } from '../../../services/storefrontService';
import MaterialSymbol from '../../common/MaterialSymbol';

const EMPTY: StorefrontConfigDto = {
    publicSlug: '',
    enabled: false,
    displayName: null,
    heroTitle: null,
    heroSubtitle: null,
    heroDescription: null,
    heroImageUrl: null,
    features: [],
    faq: [],
    testimonials: [],
    allowGuestCheckout: true,
    allowFreeEnrollment: true,
    termsUrl: null,
    privacyPolicyUrl: null,
    customCss: null,
    seoTitle: null,
    seoDescription: null,
};

type TextKey = 'displayName' | 'heroTitle' | 'heroSubtitle' | 'heroDescription' | 'heroImageUrl' | 'termsUrl' | 'privacyPolicyUrl' | 'seoTitle' | 'seoDescription' | 'customCss';

interface ListEditorProps<T extends Record<string, string>> {
    label: string;
    items: T[];
    fields: { key: keyof T & string; label: string; multiline?: boolean }[];
    blank: T;
    addLabel: string;
    removeLabel: string;
    onChange: (items: T[]) => void;
}

function ListEditor<T extends Record<string, string>>({ label, items, fields, blank, addLabel, removeLabel, onChange }: ListEditorProps<T>) {
    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>
            <Stack spacing={2}>
                {items.map((item, index) => (
                    <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
                        {fields.map((f) => (
                            <TextField
                                key={f.key}
                                size="small"
                                fullWidth
                                multiline={f.multiline}
                                label={f.label}
                                value={item[f.key] ?? ''}
                                onChange={(e) => onChange(items.map((it, i) => (i === index ? { ...it, [f.key]: e.target.value } : it)))}
                            />
                        ))}
                        <IconButton aria-label={`${removeLabel} ${index + 1}`} onClick={() => onChange(items.filter((_, i) => i !== index))}>
                            <MaterialSymbol name="delete" />
                        </IconButton>
                    </Stack>
                ))}
                <Box>
                    <Button size="small" startIcon={<MaterialSymbol name="add" />} onClick={() => onChange([...items, { ...blank }])}>{addLabel}</Button>
                </Box>
            </Stack>
        </Box>
    );
}

/** Konfiguration der Storefront (Company-Admin): Adresse, Hero, Vorteile, FAQ, Checkout-Optionen, SEO. */
const StorefrontConfigForm: React.FC<{ onSaved?: (slug: string, enabled: boolean) => void }> = ({ onSaved }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [config, setConfig] = useState<StorefrontConfigDto | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [saving, setSaving] = useState(false);
    const tr = (key: string) => translate(`storefront.admin.settings.${key}`);

    useEffect(() => {
        storefrontAdminApi
            .getConfig()
            .then(({ config: existing, suggestedSlug }) => {
                setConfig(existing ?? { ...EMPTY, publicSlug: suggestedSlug ?? '' });
                if (existing) onSaved?.(existing.publicSlug, existing.enabled);
            })
            .catch(() => setLoadError(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loadError) return <Alert severity="error">{tr('loadError')}</Alert>;
    if (!config) return <CircularProgress />;

    const text = (key: TextKey, extra: Partial<React.ComponentProps<typeof TextField>> = {}) => (
        <TextField
            fullWidth
            label={tr(key)}
            value={config[key] ?? ''}
            onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
            {...extra}
        />
    );

    const save = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const saved = await storefrontAdminApi.saveConfig(config);
            setConfig(saved);
            notify(tr('saved'), { type: 'success' });
            onSaved?.(saved.publicSlug, saved.enabled);
        } catch (err: any) {
            notify(err?.response?.data?.message || err?.response?.data?.detail || tr('saveError'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box component="form" onSubmit={save} sx={{ maxWidth: 860 }}>
            <Stack spacing={3}>
                <Box>
                    <FormControlLabel
                        control={<Switch checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} />}
                        label={tr('enabled')}
                    />
                    <Typography variant="body2" color="text.secondary">{tr('enabledHelp')}</Typography>
                </Box>

                <TextField
                    required
                    label={tr('slug')}
                    helperText={tr('slugHelp')}
                    value={config.publicSlug}
                    onChange={(e) => setConfig({ ...config, publicSlug: e.target.value.toLowerCase() })}
                    slotProps={{ htmlInput: { maxLength: 60, pattern: '[a-z0-9][a-z0-9-]{1,58}[a-z0-9]' } }}
                />
                {text('displayName')}
                {text('heroTitle')}
                {text('heroSubtitle')}
                {text('heroDescription', { multiline: true, minRows: 3 })}
                {text('heroImageUrl')}

                <ListEditor
                    label={tr('features')}
                    items={config.features}
                    fields={[{ key: 'title', label: tr('featureTitle') }, { key: 'description', label: tr('featureDescription') }]}
                    blank={{ title: '', description: '', icon: 'check_circle' }}
                    addLabel={tr('add')}
                    removeLabel={tr('remove')}
                    onChange={(features) => setConfig({ ...config, features })}
                />
                <ListEditor
                    label={tr('faq')}
                    items={config.faq}
                    fields={[{ key: 'question', label: tr('question') }, { key: 'answer', label: tr('answer'), multiline: true }]}
                    blank={{ question: '', answer: '' }}
                    addLabel={tr('add')}
                    removeLabel={tr('remove')}
                    onChange={(faq) => setConfig({ ...config, faq })}
                />
                <ListEditor
                    label={tr('testimonials')}
                    items={config.testimonials}
                    fields={[{ key: 'quote', label: tr('quote'), multiline: true }, { key: 'author', label: tr('author') }, { key: 'role', label: tr('role') }]}
                    blank={{ quote: '', author: '', role: '' }}
                    addLabel={tr('add')}
                    removeLabel={tr('remove')}
                    onChange={(testimonials) => setConfig({ ...config, testimonials })}
                />

                <Stack>
                    <FormControlLabel control={<Switch checked={config.allowGuestCheckout} onChange={(e) => setConfig({ ...config, allowGuestCheckout: e.target.checked })} />} label={tr('allowGuestCheckout')} />
                    <FormControlLabel control={<Switch checked={config.allowFreeEnrollment} onChange={(e) => setConfig({ ...config, allowFreeEnrollment: e.target.checked })} />} label={tr('allowFreeEnrollment')} />
                </Stack>
                {text('termsUrl')}
                {text('privacyPolicyUrl')}
                {text('seoTitle')}
                {text('seoDescription', { multiline: true, minRows: 2 })}
                {text('customCss', { multiline: true, minRows: 3, slotProps: { htmlInput: { style: { fontFamily: 'monospace' } } } })}

                <Box>
                    <Button type="submit" variant="contained" disabled={saving || !config.publicSlug}>{tr('save')}</Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default StorefrontConfigForm;
